import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/state/auth";
import { ApiError } from "@/lib/api";
import { Alert, Button, Field } from "@/components/ui";
import { LogoMark } from "@/components/Logo";

export function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signIn(email, password);
      navigate("/app");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sign in. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Book and track your WeShip deliveries.">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}
        <Field
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Button type="submit" loading={busy} className="w-full">
          Sign in
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-ink-400">
        New to WeShip?{" "}
        <Link to="/register" className="font-medium text-brand">
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}

export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 flex items-center gap-2 text-lg font-semibold tracking-tight">
            <LogoMark className="h-7 w-7" />
            WeShip
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mb-8 mt-1 text-sm text-ink-400">{subtitle}</p>
          {children}
        </div>
      </div>

      <aside className="relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex">
        <img
          src="/marketing/handoff.jpg"
          alt="A WeShip driver handing a parcel to a customer at her door"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-ink-950/20" />

        <p className="relative text-sm text-ink-200">Logistics, simplified.</p>
        <div className="relative">
          <p className="text-3xl font-semibold leading-tight tracking-tight">
            One account, one waybill, one place to look.
          </p>
          <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-white/20 pt-8 text-sm">
            <div>
              <dt className="text-ink-200">Coverage</dt>
              <dd className="mt-1 text-xl font-semibold">Nationwide</dd>
            </div>
            <div>
              <dt className="text-ink-200">Live rates</dt>
              <dd className="mt-1 text-xl font-semibold">At checkout</dd>
            </div>
          </dl>
        </div>
        <p className="relative text-xs text-ink-300">Rates shown include VAT. Collection cut-offs apply per service.</p>
      </aside>
    </div>
  );
}
